import * as React from "react"
import { useEffect, useRef, useState } from "react"
import { connect } from "react-redux"

import { isEqual } from "lodash"

import { ActionAnchor, ActionType } from "@tenforce/toolbox-button-push-react"
import { Modal } from "@tenforce/toolbox-modal-react"
import { Symbols } from "@tenforce/toolbox-fontmap"

import { GlobalState } from "../../../store"
import {
    getQueryParam,
    hasQueryParam,
    removeQueryParam,
    setQueryParam,
} from "../../../util/Url"

import {
    DispatchProps,
    initializeWithDictionaryVisible,
    mapDispatchToProps,
} from "../actions"

import BottomBar from "./BottomBar"
import DictionaryPopup from "./help/dictionary/DictionaryPopup"
import Tooltips from "./help/tooltip/Tooltips"

import { Labels } from "../../../util/Translations"
import { ContextualHelpDictionary, ContextualHelpTerm } from "../../../models"
import { IContextualHelp } from "../index"
import { useAppDispatch } from "../../../hooks"
import localStorage from "../../../util/LocalStorage"
import { ITenForceWindow } from "../../../global"

declare let window: ITenForceWindow

interface StateProps extends IContextualHelp {
    helpUrl: string
}

type Props = StateProps & DispatchProps

export const ContextualHelpButton = ({
    filterTerms,
    helpUrl,
    dictionaries,
    isDictionaryPopupVisible,
    isTooltipsOverlayVisible,
    updateDictionaryPopupVisibility,
    updateTooltipsOverlayVisibility,
    selectTermInTooltips,
    tooltipsOverlay: { selectedTerm, supportMailAddress },
}: Props): JSX.Element => {
    const { ContextualHelp } = Labels
    const [openedTooltip, setOpenedTooltip] = useState<Element | null>(null)
    const [visibleTooltips, setVisibleTooltips] = useState<
        [Element | null, ContextualHelpDictionary[]][]
    >([])
    const visibleTooltipsRef =
        useRef<[Element | null, ContextualHelpDictionary[]][]>(visibleTooltips)

    const dispatch = useAppDispatch()

    useEffect(() => {
        let openedTooltip = null

        if (selectedTerm && selectedTerm.target) {
            openedTooltip = document.querySelector(selectedTerm.target)
        }

        setOpenedTooltip(openedTooltip)
    }, [selectedTerm, selectedTerm?.target])

    useEffect(() => {
        getVisibleTooltips()
        const intervalId = setInterval(getVisibleTooltips, 1000)
        return () => {
            return clearInterval(intervalId)
        }
    }, [])

    useEffect(() => {
        if (hasQueryParam("dictionary")) {
            setTimeout(() => {
                const selectedTermCode = getQueryParam("dictionary")
                dispatch(
                    initializeWithDictionaryVisible(selectedTermCode as string),
                )
            }, 500)
        }
    }, [])

    useEffect(() => {
        visibleTooltipsRef.current = visibleTooltips
    }, [visibleTooltips])

    useEffect(() => {
        let tooltipToOpen:
            | [Element | null, ContextualHelpDictionary[]]
            | null
            | undefined = null
        let targetToOpen: Element | null = null,
            termToOpen: ContextualHelpTerm | null = null
        if (hasQueryParam("tooltip")) {
            const selectedTermCode = getQueryParam("tooltip")
            tooltipToOpen = visibleTooltips.find(([_target, dictionaries]) =>
                dictionaries.some((dict) => {
                    termToOpen =
                        dict.terms.find(
                            (term) => term.code === selectedTermCode,
                        ) ?? null
                    return !!termToOpen
                }),
            )
            targetToOpen = tooltipToOpen?.[0] ?? null
        } else if (window.env.shouldHighlightContextualHelpTerms) {
            tooltipToOpen = visibleTooltips.find(([_target, dictionaries]) =>
                dictionaries.some((dict) => {
                    termToOpen =
                        dict.terms.find(
                            (term) =>
                                term.isHighlighted &&
                                !localStorage.getItem(
                                    `${window.env.loggedOnPersonId}_contextual-help-term_${term.code}`,
                                ),
                        ) ?? null
                    return !!termToOpen
                }),
            )
            targetToOpen = tooltipToOpen?.[0] ?? null
        }
        if (targetToOpen && termToOpen) {
            setTimeout(() => {
                setTooltip(targetToOpen, termToOpen)
            }, 500)
        }
    }, [visibleTooltips])

    const getVisibleTooltips = (): void => {
        const newVisibleTooltips = new Map<
            Element | null,
            ContextualHelpDictionary[]
        >()

        dictionaries.forEach((dictionary) => {
            // separate terms per visible elements
            const visibleTerms = new Map<Element | null, ContextualHelpTerm[]>()

            dictionary.terms
                .filter((term) => term.target !== undefined)
                .forEach((term) => {
                    const target = document.querySelector(term.target as string)
                    if (target) {
                        // make sure that the target is visible and not only part of the DOM
                        // https://developer.mozilla.org/en-US/docs/web/api/htmlelement/offsetparent
                        // offsetParent returns null in the following situations:
                        // - The element or its parent element has the display property set to none.
                        // - The element has the position property set to fixed (Firefox returns <body>).
                        // - The element is <body> or <html>.
                        const style = window.getComputedStyle(target)
                        if (
                            style.position === "fixed" ||
                            !!(target as HTMLElement).offsetParent
                        ) {
                            visibleTerms.set(target, [
                                // add the old terms too
                                ...(visibleTerms.get(target) ?? []),
                                term,
                            ])
                        }
                    }
                })

            // convert the map to an array of [target, term[]]
            Array.from(visibleTerms.entries()).forEach((value) => {
                const target = value[0]
                const terms = value[1]

                // save the whole dictionary source
                // with only a subset of the terms
                // that are relevant for the target
                newVisibleTooltips.set(target, [
                    // add the old terms too
                    ...(newVisibleTooltips.get(target) ?? []),
                    {
                        ...dictionary,
                        terms,
                    },
                ])
            })
        })

        // convert the map to an array of [target, DictionarySource[]]
        const arrayTooltips = Array.from(newVisibleTooltips.entries())

        if (!isEqual(visibleTooltipsRef.current, arrayTooltips)) {
            setVisibleTooltips(arrayTooltips)
        }
    }

    const onOverlayClicked = (): boolean => {
        if (isDictionaryPopupVisible) {
            hideDictionary()
            filterTerms("")
            return false
        }
        return true
    }

    const hideDictionary = (): void => {
        updateDictionaryPopupVisibility(false)
    }

    const showDictionary = (): void => {
        updateDictionaryPopupVisibility(true)
    }

    const showTooltipsOverlay = (): void => {
        updateTooltipsOverlayVisibility(true)
    }

    const hideTooltipsOverlay = (): void => {
        updateTooltipsOverlayVisibility(false)
    }

    const setTooltip = (
        element: Element | null,
        term: ContextualHelpTerm | null,
    ): void => {
        if (element === null || term === null) {
            removeQueryParam("tooltip")
            selectTermInTooltips(null)
        } else {
            setQueryParam("tooltip", term.code)
            selectTermInTooltips(term)
        }
    }

    return (
        <>
            <ActionAnchor
                id="contextualHelpButton"
                title={ContextualHelp}
                actionType={ActionType.icon}
                extraClasses="is-inverted no-underline"
                onClick={(): void => showTooltipsOverlay()}
            >
                {Symbols.ContextualHelp}
            </ActionAnchor>

            <Modal
                isShown={isTooltipsOverlayVisible}
                containerProps={{
                    id: "contextualHelpModal",
                    willClose: onOverlayClicked,
                    onClose: hideTooltipsOverlay,
                    isCenterPositioned: isDictionaryPopupVisible,
                }}
                contentProps={{
                    shouldScrollBackToOrigin: false,
                    extraClasses: isDictionaryPopupVisible
                        ? "tw-mx-auto tw-my-1/20 tw-w-3/5"
                        : "",
                }}
                overlayProps={{
                    isLightColored: true,
                    "data-ft-component": "overlay-contextual-help",
                }}
            >
                {isDictionaryPopupVisible ? (
                    <DictionaryPopup />
                ) : (
                    <>
                        <Tooltips
                            tooltips={visibleTooltips}
                            openedTooltip={openedTooltip}
                            setTooltip={setTooltip}
                        />

                        <BottomBar
                            helpUrl={helpUrl}
                            showDictionary={(): void => {
                                setTooltip(null, null)
                                showDictionary()
                            }}
                            supportMailAddress={supportMailAddress}
                        />
                    </>
                )}
            </Modal>
        </>
    )
}

const mapStateToProps = (state: GlobalState): StateProps => ({
    ...state.contextualHelp,
    helpUrl: state.userInfo.helpUrl,
})

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(ContextualHelpButton)
